'use client'

import Link from 'next/link'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useLocale } from '@/lib/i18n'
import { cn } from '@/lib/utils'

interface BlogPaginationProps {
  currentPage: number
  totalPages: number
  locale: string
  basePath: string
  tagsQuery?: string
  sort?: string
  searchQuery?: string
}

export function BlogPagination({ currentPage, totalPages, locale, basePath, tagsQuery, sort, searchQuery }: BlogPaginationProps) {
  const { t } = useLocale()

  if (totalPages <= 1) return null

  const getPageUrl = (page: number) => {
    const params = new URLSearchParams()
    if (searchQuery) params.set('q', searchQuery)
    if (tagsQuery) params.set('tags', tagsQuery)
    if (sort) params.set('sort', sort)
    if (page > 1) params.set('page', String(page))
    const qs = params.toString()
    return `/blog/${locale}${basePath}${qs ? `?${qs}` : ''}`
  }

  const pages = []
  const showEllipsisStart = currentPage > 3
  const showEllipsisEnd = currentPage < totalPages - 2

  pages.push(1)

  if (showEllipsisStart) {
    pages.push('ellipsis-start')
  }

  for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
    if (!pages.includes(i)) {
      pages.push(i)
    }
  }

  if (showEllipsisEnd && !pages.includes('ellipsis-end')) {
    pages.push('ellipsis-end')
  }

  if (totalPages > 1 && !pages.includes(totalPages)) {
    pages.push(totalPages)
  }

  return (
    <nav aria-label="Blog pagination" className="flex items-center justify-center gap-1.5 mt-12">
      {currentPage > 1 ? (
        <Link
          href={getPageUrl(currentPage - 1)}
          className="flex items-center gap-1.5 rounded-lg border border-border/70 bg-card/90 px-3 py-2 text-sm font-medium text-muted-foreground transition-all duration-200 hover:border-indigo-400/50 hover:text-indigo-600 dark:hover:text-indigo-400"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('common.previous')}
        </Link>
      ) : (
        <span className="flex cursor-not-allowed items-center gap-1.5 rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-sm font-medium text-muted-foreground/40">
          <ChevronLeft className="h-4 w-4" />
          {t('common.previous')}
        </span>
      )}

      <div className="hidden sm:flex items-center gap-1">
        {pages.map((page, index) => {
          if (typeof page === 'string') {
            return (
              <span key={page} className="px-2 py-2 text-muted-foreground/50">
                ...
              </span>
            )
          }

          const isActive = page === currentPage

          return (
            <Link
              key={index}
              href={getPageUrl(page)}
              className={cn(
                'min-w-[40px] h-10 flex items-center justify-center text-sm font-medium rounded-lg transition-all duration-200',
                isActive
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                  : 'border border-border/70 bg-card/90 text-muted-foreground hover:border-indigo-400/50 hover:text-indigo-600 dark:hover:text-indigo-400'
              )}
              aria-current={isActive ? 'page' : undefined}
            >
              {page}
            </Link>
          )
        })}
      </div>

      <span className="px-4 py-2 text-sm font-medium text-muted-foreground sm:hidden">
        {t('blog.page_of', { page: currentPage, total: totalPages })}
      </span>

      {currentPage < totalPages ? (
        <Link
          href={getPageUrl(currentPage + 1)}
          className="flex items-center gap-1.5 rounded-lg border border-border/70 bg-card/90 px-3 py-2 text-sm font-medium text-muted-foreground transition-all duration-200 hover:border-indigo-400/50 hover:text-indigo-600 dark:hover:text-indigo-400"
        >
          {t('common.next')}
          <ChevronRight className="h-4 w-4" />
        </Link>
      ) : (
        <span className="flex cursor-not-allowed items-center gap-1.5 rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-sm font-medium text-muted-foreground/40">
          {t('common.next')}
          <ChevronRight className="h-4 w-4" />
        </span>
      )}
    </nav>
  )
}
